/** GENERATED_ASSET — water-sheet layer. Flow 0 paints standing water only (no sheets, streams or jet). */
import type { Ctx } from './ctx';
import type { Layer } from './svgKit';
import { Path } from './pathKit';
import type { Pt } from './pathKit';
import { CX, SLAB_D, waterCut } from './layout';
import type { TierLayout } from './layout';
import { spoutMouth } from './brass';

/** Basin surface = rounded water region minus the bottom tier's footprint (stone stays in front). */
function paintSurface(ctx: Ctx, L: Layer): void {
  const { L: lay, sw } = ctx;
  const w = lay.water, cut = waterCut(lay);
  const r = w.r;
  const cx0 = Math.max(cut.x0, w.x0 + r), cx1 = Math.min(cut.x1, w.x1 - r);
  const p = new Path().M(w.x0, w.y0 + r).Q(w.x0, w.y0, w.x0 + r, w.y0);
  p.L(cx0, w.y0).L(cx0, cut.y).L(cx1, cut.y).L(cx1, w.y0);
  p.L(w.x1 - r, w.y0).Q(w.x1, w.y0, w.x1, w.y0 + r).L(w.x1, w.y1 - r).Q(w.x1, w.y1, w.x1 - r, w.y1);
  p.L(w.x0 + r, w.y1).Q(w.x0, w.y1, w.x0, w.y1 - r).Z();
  const fill = L.linear('basin-water', 0, w.y0, 0, w.y1, [
    { offset: 0, color: sw.water.deep }, { offset: 0.55, color: sw.water.mid }, { offset: 1, color: sw.water.shallow },
  ]);
  L.path(p, { fill, part: 'basin-water' });
  // Darker reflection of the tier base, and a soft far-edge line.
  const t0 = lay.tiers[0]!;
  L.smooth([{ x: t0.x0 + 8, y: cut.y }, { x: CX - 20, y: cut.y + 3.2 }, { x: CX + 20, y: cut.y + 3.4 }, { x: t0.x1 - 8, y: cut.y }],
    { fill: sw.water.deep, fo: 0.4, part: 'tier-reflection' });
  L.line(w.x0 + r, w.y0 + 0.9, cx0 - 0.5, w.y0 + 0.9, { stroke: sw.water.edge, sw: 1, so: 0.55, part: 'far-edge' });
  L.line(cx1 + 0.5, w.y0 + 0.9, w.x1 - r, w.y0 + 0.9, { stroke: sw.water.edge, sw: 1, so: 0.55, part: 'far-edge' });
}

const sheetFill = (ctx: Ctx, L: Layer, y0: number, y1: number): string =>
  L.linear('sheet', 0, y0, 0, y1, [
    { offset: 0, color: ctx.sw.water.sheetLight, opacity: 0.95 }, { offset: 0.5, color: ctx.sw.water.sheet, opacity: 0.92 }, { offset: 1, color: ctx.sw.water.mid, opacity: 0.88 },
  ]);

function sheetShape(t: TierLayout, f: number): { path: Path; yTop: number; yBot: number; wTop: number; wBot: number } {
  const wTop = t.notchW * (0.5 + 0.34 * f), wBot = Math.min(t.notchW * 1.02, wTop * 1.12 + 2);
  const yTop = t.s + 0.8, yBot = t.base + 2.2;
  const x = t.lipX, hw = wTop / 2, bw = wBot / 2, mid = (yTop + yBot) / 2;
  const p = new Path().M(x - hw, yTop)
    .C(x - hw - 0.8, mid - 4, x - bw + 1.2, mid + 6, x - bw, yBot - 2)
    .Q(x - bw * 0.62, yBot + 1.2, x - bw * 0.3, yBot - 0.4)
    .Q(x, yBot + 1.5, x + bw * 0.3, yBot - 0.4)
    .Q(x + bw * 0.62, yBot + 1.2, x + bw, yBot - 2)
    .C(x + bw - 1.2, mid + 6, x + hw + 0.8, mid - 4, x + hw, yTop)
    .Q(x, yTop + 2.2, x - hw, yTop).Z();
  return { path: p, yTop, yBot, wTop, wBot };
}

function paintSheet(ctx: Ctx, L: Layer, t: TierLayout): void {
  const { sw, p } = ctx;
  const f = p.flow;
  const s = sheetShape(t, f);
  L.path(s.path, { fill: sheetFill(ctx, L, s.yTop, s.yBot), stroke: sw.water.edge, sw: 1.2, so: 0.85, part: `fall${t.k}-sheet` });
  // Water bulging over the lip, plus a bright left edge.
  const hw = s.wTop / 2;
  L.path(new Path().M(t.lipX - hw + 1.6, s.yTop + 0.4).Q(t.lipX, s.yTop + 2.9, t.lipX + hw - 1.6, s.yTop + 0.4), { stroke: sw.water.sheetLight, sw: 1.5, so: 0.95, part: 'lip-bulge' });
  // Streaks: staggered light/dark vertical strokes that stay inside the sheet.
  const r = ctx.rng(`sheet.streaks.${t.k}`);
  const n = 2 + Math.round(f * 3);
  const H = s.yBot - s.yTop;
  for (let i = 0; i < 5; i++) {
    const u = (i + 0.5) / 5, jitter = r.range(-0.08, 0.08);
    const len = Math.min(H - 8, r.range(12, 22)), base = r.range(0, Math.max(1, H - len - 6));
    const dark = i % 2 === 1;
    if (i >= n) continue;
    const off = ctx.phase === undefined ? base : (base + ctx.phase * (H - len - 6)) % Math.max(1, H - len - 6);
    const half = (s.wTop / 2 - 2.6) * (1 - 2 * Math.abs(u - 0.5) * 0.15);
    const x = t.lipX + (u - 0.5 + jitter) * 2 * half;
    const y0 = s.yTop + 3 + off;
    L.line(x, y0, x + (x - t.lipX) * 0.05, y0 + len, dark ? { stroke: sw.water.deep, sw: 1.2, so: 0.38, part: 'streak-dark' } : { stroke: sw.water.sheetLight, sw: 1.5, so: 0.8, part: 'streak' });
  }
}

/** Flow along a slab surface from where the upper fall lands to this tier's spillway notch. */
function paintStream(ctx: Ctx, L: Layer, t: TierLayout, from: Pt): void {
  const { sw, p } = ctx;
  const to: Pt = { x: t.lipX, y: t.s + 2 };
  const wA = 4.5 + 3.5 * p.flow, wB = Math.max(5, t.notchW * (0.26 + 0.16 * p.flow));
  const N = 14;
  // Centerline: spreads flat across the slab, then bends down into the spillway notch.
  const c: Pt[] = [];
  for (let i = 0; i <= N; i++) {
    const u = i / N, e = u * u * (3 - 2 * u);
    c.push({ x: from.x + (to.x - from.x) * e, y: from.y + (to.y - from.y) * Math.pow(u, 2.6) });
  }
  const left: Pt[] = [], right: Pt[] = [], mid: Pt[] = c;
  for (let i = 0; i <= N; i++) {
    const a = c[Math.max(0, i - 1)]!, b = c[Math.min(N, i + 1)]!;
    const tx = b.x - a.x, ty = b.y - a.y, len = Math.hypot(tx, ty) || 1;
    const u = i / N, hw = (wA + (wB - wA) * Math.pow(u, 2)) / 2;
    left.push({ x: c[i]!.x + (ty / len) * hw, y: c[i]!.y - (tx / len) * hw });
    right.push({ x: c[i]!.x - (ty / len) * hw, y: c[i]!.y + (tx / len) * hw });
  }
  L.path(new Path().poly([...left, ...right.reverse()]), { fill: sheetFill(ctx, L, from.y, to.y + 2), stroke: sw.water.edge, sw: 1, so: 0.8, part: `stream${t.k}` });
  L.path(new Path().poly(mid.slice(1, -1), false), { stroke: sw.water.sheetLight, sw: 1.3, so: 0.75, part: 'stream-glint' });
}

function paintJet(ctx: Ctx, L: Layer): void {
  const { sw, p, L: lay } = ctx;
  const m = spoutMouth(ctx), yEnd = lay.stele.jetLandY;
  const wTop = 3.4 + 2.2 * p.flow, wBot = wTop + 2.2;
  const path = new Path().M(m.x - wTop / 2, m.y).C(m.x - wTop / 2 - 0.4, m.y + 8, m.x - wBot / 2 + 0.4, yEnd - 8, m.x - wBot / 2, yEnd)
    .L(m.x + wBot / 2, yEnd).C(m.x + wBot / 2 - 0.4, yEnd - 8, m.x + wTop / 2 + 0.4, m.y + 8, m.x + wTop / 2, m.y).Z();
  L.path(path, { fill: sheetFill(ctx, L, m.y, yEnd), stroke: sw.water.edge, sw: 1.1, so: 0.85, part: 'source-jet' });
  L.line(m.x - 0.7, m.y + 3, m.x - 0.7, yEnd - 5, { stroke: sw.water.sheetLight, sw: 1.1, so: 0.85, part: 'jet-glint' });
}

export function paintWater(ctx: Ctx, L: Layer): void {
  const { L: lay, p } = ctx;
  if (lay.water.present) paintSurface(ctx, L);
  if (p.flow <= 0) return;
  for (let k = lay.T - 1; k >= 0; k--) {
    const t = lay.tiers[k]!;
    const from: Pt = k === lay.T - 1 ? { x: CX, y: lay.stele.jetLandY } : { x: lay.tiers[k + 1]!.lipX, y: t.s - SLAB_D + 3.5 };
    paintStream(ctx, L, t, from);
  }
  paintJet(ctx, L);
  for (let k = lay.T - 1; k >= 0; k--) paintSheet(ctx, L, lay.tiers[k]!);
}
